import { useCallback, useMemo, useState } from "react";
import { currentFeatures } from "@/configs/Settings";
import { oids } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import {
  useMessagingTemplatesForSend,
  type MessagingAppointment,
  type MessagingPatient,
  type MessagingTemplate,
} from "src-next/modules/crm-clinical/messaging";

function pickPatientName(patient: fhir5.Patient): string {
  const nameObj =
    patient.name?.find((n) => n.use === "usual" || n.use === "official") ??
    patient.name?.[0];
  const fromText = String(nameObj?.text ?? "").trim();
  if (fromText) return fromText.slice(0, 200);
  const fromGiven = nameObj?.given?.join(" ")?.trim();
  return (fromGiven || "Paciente").slice(0, 200);
}

function pickPatientCpf(patient: fhir5.Patient): string | undefined {
  const fromIdentifier =
    patient.identifier?.find((entry) =>
      String(entry.system ?? "").includes("cpf")
    )?.value ?? patient.identifier?.[0]?.value;
  const fromId =
    patient.id && String(patient.id).includes("-")
      ? String(patient.id).split("-")[1]
      : "";
  const cpf = String(fromIdentifier ?? fromId ?? "").trim();
  return cpf || undefined;
}

function pickPatientPhone(patient: fhir5.Patient): string | undefined {
  const phone = patient.telecom
    ?.find((entry) => entry.system === "phone")
    ?.value?.trim();
  return phone || undefined;
}

export function mapFhirPatientToMessagingPatient(
  patient: fhir5.Patient | null | undefined
): MessagingPatient | null {
  if (!patient?.id) return null;

  const fullName = pickPatientName(patient);
  const phone = pickPatientPhone(patient);
  const cpf = pickPatientCpf(patient);
  const isVet = !currentFeatures.defaultApp;

  if (isVet) {
    return {
      id: `tutor-of-${patient.id}`,
      fullName: fullName ? `Tutor de ${fullName}` : "Tutor",
      cpf,
      phone,
      isTutor: true,
    };
  }

  return {
    id: patient.id,
    fullName: fullName || "Paciente",
    cpf,
    phone,
  };
}

export function mapAppointmentToMessagingAppointment(
  appointment: fhir5.Appointment
): MessagingAppointment {
  return {
    id: appointment.id ?? "appointment",
    startAtIso: appointment.start ?? undefined,
  };
}

export function dedupeMessagingPatients(
  patients: MessagingPatient[]
): MessagingPatient[] {
  const seen = new Set<string>();
  const result: MessagingPatient[] = [];

  patients.forEach((patient) => {
    if (seen.has(patient.id)) return;
    seen.add(patient.id);
    result.push(patient);
  });

  return result;
}

export interface MessagingRecipientSummary {
  entries: MessagingPatient[];
  uniquePatients: MessagingPatient[];
  totalEntries: number;
  uniqueCount: number;
  duplicateEntries: number;
}

export function summarizeMessagingRecipients(
  entries: MessagingPatient[]
): MessagingRecipientSummary {
  const uniquePatients = dedupeMessagingPatients(entries);

  return {
    entries,
    uniquePatients,
    totalEntries: entries.length,
    uniqueCount: uniquePatients.length,
    duplicateEntries: entries.length - uniquePatients.length,
  };
}

export function mapAppointmentRowsToMessagingPatientEntries(
  rows: Array<{ appointment: fhir5.Appointment }>,
  patients: fhir5.Patient[]
): MessagingPatient[] {
  const result: MessagingPatient[] = [];

  rows.forEach((row) => {
    const patient = patients.find((entry) =>
      row.appointment.participant?.some(
        (part) => part.actor?.reference === `Patient/${entry.id}`
      )
    );
    const messagingPatient = mapFhirPatientToMessagingPatient(patient);
    if (messagingPatient) result.push(messagingPatient);
  });

  return result;
}

export type ConsultationWhatsAppSendParams = {
  patient: MessagingPatient;
  appointment: MessagingAppointment;
};

const AD_HOC_APPOINTMENT: MessagingAppointment = {
  id: "admin-insights-batch",
  startAtIso: new Date().toISOString(),
};

type WhatsAppPayload = {
  patient: MessagingPatient | null;
  appointment: MessagingAppointment;
  initialSelectedPatients?: MessagingPatient[];
  staticPatientListForSelection?: MessagingPatient[];
};

export function useConsultationWhatsAppSend() {
  const { organizationId } = useOrganizationContext();
  const { user } = useSessionContext();
  const { templates, isLoading: isTemplatesLoading } = useMessagingTemplatesForSend({
    organizationId,
  });

  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<WhatsAppPayload | null>(null);

  const openSend = useCallback((params: ConsultationWhatsAppSendParams) => {
    setPayload({
      patient: params.patient,
      appointment: params.appointment,
    });
    setOpen(true);
  }, []);

  const openSendBatch = useCallback(
    (params: {
      patients: MessagingPatient[];
      appointment?: MessagingAppointment;
    }) => {
      if (params.patients.length === 0) return;

      const appointment = params.appointment ?? AD_HOC_APPOINTMENT;

      if (params.patients.length === 1) {
        setPayload({
          patient: params.patients[0],
          appointment,
        });
      } else {
        setPayload({
          patient: null,
          appointment,
          initialSelectedPatients: params.patients,
          staticPatientListForSelection: params.patients,
        });
      }
      setOpen(true);
    },
    []
  );

  const closeSend = useCallback(() => {
    setOpen(false);
    setPayload(null);
  }, []);

  const practitionerId = user?.username
    ? `${oids.cpf}-${user.username}`
    : null;

  const sendDialogProps = useMemo(
    () => ({
      open,
      onClose: closeSend,
      appointment: payload?.appointment ?? AD_HOC_APPOINTMENT,
      patient: payload?.patient ?? null,
      templates: templates as MessagingTemplate[],
      organizationId,
      practitionerId,
      initialSelectedPatients: payload?.initialSelectedPatients,
      staticPatientListForSelection: payload?.staticPatientListForSelection,
    }),
    [open, closeSend, payload, templates, organizationId, practitionerId]
  );

  return {
    openSend,
    openSendBatch,
    isTemplatesLoading,
    hasTemplates: templates.length > 0,
    sendDialogProps,
  };
}
